// RehabCategoryModal.tsx

import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  View,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Keyboard,
  StyleSheet,
  ActivityIndicator,
} from "react-native"
import { H2, Small, H5, H6, Tiny } from "@/components/standard/BFText"
import { BFColors } from "@/constants/Colors"
import CloseButton from "@/assets/icons/close-button.svg"
import CheckCircleChecked from "phosphor-react-native/src/icons/CheckCircle"
import CheckCircleUnchecked from "phosphor-react-native/src/icons/Circle"
import { BFTextInput } from "@/components/standard/BFTextInput"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import {
  PrimaryButton,
  UnderlinedActionButton,
} from "@/components/buttons/CommonButtons"
import Spacer from "@/components/util/Spacer"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import * as RehabEvents from "@/analytics/events/rehab"
import { useUpsertBudgetLineItem, useEditRehab } from "@/queries/rehab"
import { type RehabCategory } from "@/screens/rehab/utils/Rehab"
import { showErrorToast } from "@/components/toasts/AppToast"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import {
  type CombinedLoanAndProductsQuery,
  type RehabBudgetType,
} from "@/generated/graphql"
import { stringToNumber } from "@/utils/NumberUtils"
import { type ContingencyType } from "@/types/rehabTypes"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

/**
 * Props for the RehabCategoryModal:
 * - categoryData: The local category definitions (label, options, etc.) for the selected category
 * - onKeyboardToggle: For parent to know when keyboard is open/closed
 * - onDismiss: Called when the modal should close
 * - propertyAddress: For analytics tracking
 */
interface RehabCategoryModalProps {
  loanApplication: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
  rehabBudget: RehabBudgetType
  categoryData: RehabCategory | null
  onKeyboardToggle: (isOpen: boolean) => void // Notify parent when keyboard opens/closes
  onDismiss: () => void
  propertyAddress: string
  contingencyType?: ContingencyType // Optional, only for contingency
}

const RehabCategoryModal: React.FC<RehabCategoryModalProps> = ({
  loanApplication,
  rehabBudget,
  categoryData,
  onKeyboardToggle,
  onDismiss,
  propertyAddress,
  contingencyType,
}) => {
  const isDesktop = useIsDesktop()
  const { isAndroid } = useDeviceAttributes()
  const scrollViewRef = useRef<ScrollView>(null)
  const insets = useSafeAreaInsets()
  const BOTTOM_BUTTON_PADDING_WEB = 20
  const BOTTOM_BUTTON_PADDING_IOS = 50
  const BOTTOM_BUTTON_PADDING_ANDROID = 32

  // Local state for budget (for non-contingency), selections, and additional info.
  const [budget, setBudget] = useState<number | null>(null)
  // For contingency, we store the selection as a string (e.g. "0.05");
  // For non-contingency, this might be an array or string.
  const [selectedOptions, setSelectedOptions] = useState<string | string[]>(
    categoryData && categoryData.selectType === "single" ? "" : []
  )
  const [additionalInfo, setAdditionalInfo] = useState<string>("")
  const [pseudoSaveSelected, setPseudoSaveSelected] = useState<boolean>(false)
  const [userHasCleared, setUserHasCleared] = useState(false)
  const isContingency = contingencyType !== undefined

  // Get values from the rehabBudget
  const subtotal: number = stringToNumber(rehabBudget?.subtotal || "0") ?? 0

  // In non-contingency, we might display an existing contingencyValue.
  // But for contingency mode, we want to calculate: subtotal * selectedRate.
  const selectedContingencyRate = useMemo(() => {
    if (!isContingency) return 0

    if (typeof selectedOptions === "string" && selectedOptions.trim() !== "") {
      const parsed = parseFloat(selectedOptions)
      return isNaN(parsed) ? 0 : parsed
    }

    if (Array.isArray(selectedOptions) && selectedOptions.length > 0) {
      const parsed = parseFloat(selectedOptions[0])
      return isNaN(parsed) ? 0 : parsed
    }

    return 0
  }, [isContingency, selectedOptions])

  const displayedBudget = isContingency
    ? subtotal * selectedContingencyRate
    : budget

  const { lineItemsWithUserData, isLoading, isEditable } =
    useRehabBudgetingFeature(
      // error
      loanApplication,
      isDesktop
    )

  const { mutate: upsertLineItem, isPending: isLineItemUpdating } =
    useUpsertBudgetLineItem(rehabBudget.id)

  // Use the useEditRehab hook to update the higher–level rehab budget (including contingencyAmount)
  const { mutate: editRehab } = useEditRehab()

  useEffect(() => {
    RehabEvents.rehabCategoryViewed({
      propertyAddress,
      category: categoryData?.label || "Unknown Category",
    })

    const keyboardDidShowListener = Keyboard.addListener(
      "keyboardDidShow",
      () => {
        onKeyboardToggle(true)
        scrollViewRef.current?.scrollToEnd({ animated: true })
      }
    )

    const keyboardDidHideListener = Keyboard.addListener(
      "keyboardDidHide",
      () => {
        onKeyboardToggle(false)
      }
    )

    // Clean up the event listeners when the component is unmounted
    return () => {
      onKeyboardToggle(false)
      keyboardDidShowListener.remove()
      keyboardDidHideListener.remove()
    }
  }, [])

  if (!categoryData) {
    return (
      <View className="flex flex-col items-center justify-center">
        <Small>Category unavailable</Small>
      </View>
    )
  }

  // Pre-populate budget/memo/selection from the line items with user data, if we find a match
  useEffect(() => {
    if (!categoryData) return

    if (isContingency) {
      setSelectedOptions(contingencyType.amount.toString())
    } else {
      // For non-contingency, populate normally.
      const userItem = lineItemsWithUserData.find(
        (item) => item.fieldName === categoryData.fieldName
      )
      if (userItem) {
        setBudget(userItem.budget)
        setAdditionalInfo(userItem.memo || "")
        if (Array.isArray(userItem.userSelections)) {
          setSelectedOptions(userItem.userSelections)
        } else if (typeof userItem.userSelections === "string") {
          setSelectedOptions(userItem.userSelections)
        } else {
          setSelectedOptions([])
        }
      } else {
        setBudget(0)
        setAdditionalInfo("")
        setSelectedOptions([])
      }
    }
  }, [
    categoryData,
    lineItemsWithUserData,
    isContingency,
    rehabBudget,
    contingencyType,
  ])

  // Utility: get the fieldName for an option.
  const getOptionValue = (option: { fieldName: string }) => option.fieldName

  // When the user taps an option
  const handleOptionPress = (optionValue: string) => {
    if (!isEditable) return
    if (categoryData.selectType === "single") {
      if (isContingency) {
        setSelectedOptions(optionValue)
      } else {
        setSelectedOptions([optionValue])
      }
    } else {
      setSelectedOptions((prev) =>
        Array.isArray(prev)
          ? prev.includes(optionValue)
            ? prev.filter((opt) => opt !== optionValue)
            : [...prev, optionValue]
          : [prev, optionValue].filter(Boolean)
      )
    }

    RehabEvents.rehabCategoryOptionClicked({
      propertyAddress,
      category: categoryData.label,
      option: optionValue,
    })
  }

  const normalizedSelections =
    typeof selectedOptions === "string" ? [selectedOptions] : selectedOptions

  // Only require additional information if any of the selected options require details or the highest level
  // of the category's data requires it.
  const requiresDetails = categoryData.options.some(
    (option) =>
      option.requiresDetails &&
      normalizedSelections.includes(getOptionValue(option))
  )

  // Determine if the Additional Information block should be shown
  const showAdditionalInfoBlock =
    requiresDetails || categoryData.requiresDetails

  // Derive "cleared" state from current inputs
  const isCleared = useMemo(() => {
    const isBudgetCleared = budget === 0 || budget === null
    const isAdditionalCleared = additionalInfo.trim() === ""
    const isSelectionCleared = isContingency
      ? selectedOptions === "0.02"
      : Array.isArray(selectedOptions)
        ? selectedOptions.length === 0
        : selectedOptions === ""
    return (
      isBudgetCleared &&
      isAdditionalCleared &&
      isSelectionCleared &&
      userHasCleared
    )
  }, [budget, additionalInfo, selectedOptions, isContingency, userHasCleared])

  const budgetIsValid =
    isContingency || isCleared ? true : budget !== null && budget > 0

  const multiSelected = Array.isArray(selectedOptions)
    ? selectedOptions
    : [selectedOptions]

  const getOptionsAreValid = (): boolean => {
    if (isCleared) {
      return true
    }
    // If there are no options, consider the selection valid.
    if (categoryData.options.length === 0) {
      return true
    }
    if (categoryData.selectType === "single") {
      if (typeof selectedOptions === "string") {
        return selectedOptions !== ""
      }
      // If it's an array (unexpected for single-select), treat it as invalid if empty.
      return Array.isArray(selectedOptions) && selectedOptions.length > 0
    }
    return multiSelected.length > 0
  }

  const optionsAreValid = getOptionsAreValid()

  const requirementsAreValid =
    (((requiresDetails && additionalInfo.trim() !== "") || !requiresDetails) &&
      optionsAreValid &&
      budgetIsValid) ||
    isCleared

  const isSaveDisabled = !requirementsAreValid
  const requiredColor = pseudoSaveSelected
    ? "text-red-orange"
    : "text-midnight-blue"

  const handleSave = async () => {
    if (!requirementsAreValid) {
      setPseudoSaveSelected(true)
      return
    }
    try {
      // Determine final selection based on selection type:
      // For multi-select, always send an array (even if one item is selected)
      // For single-select, send a single string
      const finalSelection =
        categoryData.selectType === "multi"
          ? Array.isArray(selectedOptions)
            ? selectedOptions
            : [selectedOptions]
          : Array.isArray(selectedOptions)
            ? selectedOptions[0]
            : selectedOptions

      if (!isContingency) {
        // Upsert the budget line item without including contingencyAmount.
        const lineItemInput = {
          rehabBudgetId: rehabBudget.id,
          category: categoryData.fieldName,
          budget: budget ?? 0,
          memo: additionalInfo,
          selection: finalSelection === undefined ? null : finalSelection, // Ensures clear all works
        }
        if (isEditable) {
          console.info(
            "👍Upserting budget line item with input:",
            lineItemInput
          )
          upsertLineItem(lineItemInput)
        }
      }

      // For contingency modals, update the higher-level rehab budget's contingencyAmount.
      if (isContingency) {
        // When saving, compute the contingency rate from the selected option.
        let selectedRate: number | undefined
        if (typeof selectedOptions === "string" && selectedOptions !== "") {
          selectedRate = parseFloat(selectedOptions)
        } else if (
          Array.isArray(selectedOptions) &&
          selectedOptions.length > 0
        ) {
          selectedRate = parseFloat(selectedOptions[0])
        }
        if (selectedRate && isEditable) {
          editRehab({
            rehabBudgetId: rehabBudget.id,
            rehabInput: { contingencyAmount: selectedRate },
          })
        }
      }

      onDismiss()
    } catch (error) {
      console.error("Error saving line item", error)
      showErrorToast({
        title: "Line Item Error",
        message: (error as any).message,
      })
    }
  }

  const isMisc: boolean =
    categoryData.fieldName === "misc_1" || categoryData.fieldName === "misc_2"

  const styles = StyleSheet.create({
    saveButtonContainer: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: isDesktop
        ? BOTTOM_BUTTON_PADDING_WEB
        : isAndroid
          ? BOTTOM_BUTTON_PADDING_ANDROID
          : BOTTOM_BUTTON_PADDING_IOS,
      paddingHorizontal: 16,
      alignItems: "center",
      justifyContent: "center",
    },
  })

  if (isLoading) {
    return (
      <ActivityIndicator
        className="flex-1 justify-center items-center pt-10"
        size="small"
      />
    )
  }

  if (!rehabBudget.id) {
    return null
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: BFColors.offWhite,
        paddingHorizontal: 16,
      }}
    >
      {/* Header with Close and Clear All Buttons */}
      <View className="flex-row h-[40px] items-start">
        {/* Clear All Button */}
        <UnderlinedActionButton
          title="Clear All"
          testID="clear-all-button"
          disabled={
            (isContingency && selectedOptions === "0.02") || !isEditable
          }
          onPress={() => {
            setSelectedOptions(isContingency ? "0.02" : [])
            setPseudoSaveSelected(false)
            additionalInfo && setAdditionalInfo("")
            setBudget(0)
            setUserHasCleared(true)
          }}
        />

        <Spacer />

        {/* Close Button */}
        <TouchableOpacity
          testID="close-button"
          hitSlop={42}
          onPress={onDismiss}
        >
          <CloseButton color={BFColors.lightest} width={13} height={13} />
        </TouchableOpacity>
      </View>

      {/* Modal Content */}
      <KeyboardAvoidingView className="flex-1 bg-off-white" behavior="padding">
        <ScrollView
          ref={scrollViewRef}
          className="flex-1 grow shrink"
          contentContainerStyle={{
            marginBottom: isDesktop ? insets.bottom + 90 : 148,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* Title */}
          <H2
            className="text-midnight-blue mt-2"
            style={{ minHeight: 68, textAlign: "center" }}
          >
            {categoryData.longLabel
              ? categoryData.longLabel
              : categoryData.label}
          </H2>

          {/* Budget Input */}
          <View className="mb-6">
            <CalculatorItem
              title="Budget"
              value={isContingency ? displayedBudget : budget}
              onValueChange={
                !isContingency && isEditable
                  ? (val: React.SetStateAction<number | null>) => {
                      setBudget(val)
                    }
                  : undefined
              }
              prefix="$"
              precision={0}
              variant="modal"
              noMin
            />

            {/* Error Message */}
            {!budgetIsValid && pseudoSaveSelected && (
              <Tiny className={`h-4 ${requiredColor}`}>
                You must enter a budget
              </Tiny>
            )}
          </View>

          {/* Options */}
          {categoryData.options.length > 0 && (
            <View className="mb-4">
              <H5>Category Detail</H5>

              {categoryData.description && (
                <Small className="my-1">{categoryData.description}</Small>
              )}

              <View className="mt-2 border-light-grey bg-lightest border rounded-[6px] pt-4 px-5 pb-5">
                {categoryData.options.map((option, index) => {
                  const optionValue = getOptionValue(option)
                  const currentSelections =
                    typeof selectedOptions === "string"
                      ? [selectedOptions]
                      : selectedOptions

                  const isSelected = Array.isArray(currentSelections)
                    ? currentSelections.includes(optionValue)
                    : currentSelections === optionValue

                  return (
                    <TouchableOpacity
                      testID={`option-${index}`}
                      key={index}
                      onPress={() => {
                        handleOptionPress(optionValue)
                      }}
                      className="flex-row justify-between items-center py-2 border-light-grey"
                      disabled={!isEditable}
                    >
                      <View className="flex-1 pr-2">
                        <View className="flex-row items-center">
                          {option.modifier && (
                            <H6
                              className="text-midnight-blue truncate"
                              numberOfLines={1}
                            >
                              {option.modifier}
                            </H6>
                          )}
                          {option.requiresDetails && option.modifier && (
                            <H6 className={requiredColor}>*</H6>
                          )}
                        </View>
                        {option.detail &&
                          (option.modifier ? (
                            <Small
                              className="text-dark-grey truncate"
                              numberOfLines={2}
                            >
                              {option.detail}
                            </Small>
                          ) : (
                            <View className="flex-row items-center">
                              <H6
                                className="text-midnight-blue truncate"
                                numberOfLines={2}
                              >
                                {option.detail}
                              </H6>
                              {option.requiresDetails && (
                                <H6 className={requiredColor}>*</H6>
                              )}
                            </View>
                          ))}
                      </View>
                      {isSelected ? (
                        <CheckCircleChecked
                          size={24}
                          weight="fill"
                          color={BFColors.calmBlue}
                        />
                      ) : (
                        <CheckCircleUnchecked
                          size={24}
                          color={BFColors.calmBlue}
                        />
                      )}
                    </TouchableOpacity>
                  )
                })}
              </View>

              {/* Error Message */}
              {!optionsAreValid && pseudoSaveSelected && (
                <Tiny className={`mt-2 h-4 ${requiredColor}`}>
                  You must select at least one or more
                </Tiny>
              )}
            </View>
          )}

          {/* Additional Information */}
          {showAdditionalInfoBlock && (
            <View className="mt-2 mb-4">
              <View className={`flex-row items-center ${requiredColor}`}>
                <H5>Additional Information</H5>
                {requiresDetails && <H5 className={requiredColor}>*</H5>}
              </View>
              {categoryData.description && isMisc && (
                <Small className="my-1">{categoryData.description}</Small>
              )}
              <BFTextInput
                multiline
                placeholder={
                  categoryData.additionalInfoPlaceholder || "Description..."
                }
                value={additionalInfo}
                onChangeText={setAdditionalInfo}
                className="mt-2 p-[10px] h-[100px] border border-light-grey bg-lightest rounded-md text-darkest text-sm"
                returnKeyType="done"
                returnKeyLabel="Done"
                submitBehavior={"blurAndSubmit"}
                readOnly={!isEditable}
              />
              {requiresDetails && additionalInfo.trim() === "" && (
                <Tiny className={`mt-2 ${requiredColor}`}>
                  *Additional information is required
                </Tiny>
              )}
            </View>
          )}
          {/* Extra Padding to allow space for keyboard */}
          {!isDesktop && <View className="h-20" />}
        </ScrollView>

        {/* Save Button - Not present when in-review */}
        {isEditable && (
          <View style={styles.saveButtonContainer}>
            <PrimaryButton
              testID="save-button"
              title="Save"
              onPress={() => {
                void handleSave().then((r) => {
                  console.log("saved rehab")
                })
              }}
              pseudoDisabled={isSaveDisabled}
              onPseudoPress={() => {
                console.log("User tapped disabled button.")
                setPseudoSaveSelected(true)
              }}
              loading={isLineItemUpdating}
            />
          </View>
        )}
      </KeyboardAvoidingView>
    </View>
  )
}

export default RehabCategoryModal
